import { PatientReminderData } from "../../types/reminderTypes";

type PatientIdentifiers = Pick<PatientReminderData, "userLBO" | "userJmbg">;

/**
 * ITAM-2716: the identifier the reminder endpoints resolve a patient by.
 *
 * Every backend lookup on the reminder chain keys on
 * COALESCE(NULLIF(LBO,''), jmbg) (Util.effectivePersonalId on the Java side),
 * and the backend calls that coalesced key "LBO" in its own DELETE route and
 * repository method names. So the delete path segment and the PUT body's
 * userLbo slot both carry this value, not the raw LBO column.
 *
 * LBO-first order is mandatory, not stylistic. For a patient holding both
 * identifiers the JMBG matches zero rows. The delete endpoint returns 200 either
 * way and cannot report a zero-row delete, so preferring the JMBG would make the
 * table and the server diverge silently.
 *
 * The trim is a behavior change for a legacy row stored with a padded user_LBO,
 * which the backend query does not trim: such a row is deletable today and would
 * stop being deletable. That is the same class as the empty-string audit tracked
 * on the backend migration.
 */
export const effectivePersonalId = (patient: PatientIdentifiers): string =>
  patient.userLBO?.trim() || patient.userJmbg?.trim() || "";

/** Which identifier effectivePersonalId picked, for labelling a read-only field. */
export const personalIdLabel = (patient: PatientIdentifiers): "LBO" | "JMBG" =>
  !patient.userLBO?.trim() && patient.userJmbg?.trim() ? "JMBG" : "LBO";

export type ReminderIdentifierMode = "create" | "edit";

/**
 * Whether the reminder modal's identifier blocks saving.
 *
 * Create mode still requires an 11-character LBO: the modal has no JMBG field,
 * and a 13-digit value sent through userLbo on POST would be stored as an LBO.
 *
 * Edit mode carries the row's effective personal id (LBO, else JMBG) in a
 * read-only field, so only a blank identifier blocks. Length is not checked:
 * a JMBG is 13 characters and the backend resolves it through the same
 * coalesced key.
 */
export const isReminderIdentifierInvalid = (
  userLbo: string | null | undefined,
  mode: ReminderIdentifierMode,
): boolean => {
  if (mode === "edit") return !userLbo?.trim();
  return !userLbo || userLbo.length !== 11;
};
